import { useEffect, useRef } from 'react';
import { formatWhen } from '../../lib/leadBot/queueView';
import { isSystemEvent } from '../../lib/leadBot/messageKinds';
import type { BotMessage } from '../../types/leadBot';

// The ONE conversation transcript, used by the Kyck Lead bot page AND the client portal's
// "Lead conversations" page, so the scrolling behaviour is fixed (and tested) once.
//
// ⚠️ SCROLL CONTRACT: this component is the ONLY scrolling region of a conversation column. It is
// `min-h-0 flex-1 overflow-y-auto`, which only scrolls when EVERY ancestor up to the shell has a
// bounded height. The root cause of the 2026-10-15 "can't scroll" bug was the page root using
// `flex-1` inside <main>, which is a plain block box, so the page grew past the viewport and
// <main>'s overflow-hidden clipped it. A host page must be `h-full min-h-0 flex flex-col` (staff:
// inside useFullHeightLayout) and every flex ancestor `min-h-0`. See transcriptScroll.test.tsx.

/** The fields a transcript needs. Both surfaces send at least these. */
export type TranscriptMessage = Pick<
  BotMessage,
  'id' | 'direction' | 'status' | 'text' | 'createdAt'
> &
  Partial<
    Pick<
      BotMessage,
      | 'sentAt'
      | 'reaction'
      | 'followUp'
      | 'fixedKind'
      | 'approvedBy'
      | 'approvedByName'
      | 'failureReason'
      | 'staffReply'
      | 'callEvent'
    >
  >;

const STATUS_META: Record<
  BotMessage['status'],
  { label: string; tone: 'muted' | 'danger' } | null
> = {
  received: null,
  pending_approval: { label: 'Awaiting approval', tone: 'muted' },
  sending: { label: 'Sending…', tone: 'muted' },
  sent: null,
  failed: { label: 'Failed to send', tone: 'danger' },
  rejected: { label: 'Rejected by a person — never sent', tone: 'danger' },
  superseded: { label: 'Superseded by a newer draft — never sent', tone: 'muted' },
};

/** True when the reader is within ~80px of the bottom of a scroll container. */
export function isNearBottom(el: {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
}): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
}

export function TranscriptBubble({
  message,
  internals,
}: {
  message: TranscriptMessage;
  /** Kyck staff view: shows approval/unreviewed/fixed-wording detail. The portal never does. */
  internals: boolean;
}) {
  // ⚠️ A SYSTEM EVENT (a missed-call note, "the club tried to call") is not the lead's words and not ours: a centred note.
  if (isSystemEvent(message)) {
    return (
      <div className="flex justify-center" data-testid="system-note">
        <p className="max-w-[85%] rounded-full border border-dashed border-border bg-muted/40 px-3 py-1 text-center text-xs italic text-muted-foreground">
          {message.text} · {formatWhen(message.createdAt)}
        </p>
      </div>
    );
  }
  const inbound = message.direction === 'inbound';
  if (message.reaction) {
    return (
      <div className="flex justify-start">
        <p className="rounded-full bg-muted px-3 py-1 text-xs italic text-muted-foreground">
          Reaction: {message.text} · {formatWhen(message.createdAt)}
        </p>
      </div>
    );
  }
  const meta = STATUS_META[message.status];
  const neverSent =
    message.status === 'rejected' ||
    message.status === 'superseded' ||
    message.status === 'pending_approval';
  const staff = message.staffReply ?? null;
  const unreviewed =
    internals && message.status === 'sent' && message.approvedBy === null && !staff;
  const staffName = staff ? (internals ? (staff.byName ?? 'Club team') : 'Club team') : null;

  return (
    <div className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
      <div className="max-w-[85%]">
        <div
          className={[
            'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words',
            inbound
              ? 'bg-muted text-foreground'
              : neverSent
                ? 'border border-dashed border-border bg-muted/40 text-muted-foreground'
                : staff
                  ? 'bg-foreground text-background'
                  : 'bg-kyck-600 text-white',
          ].join(' ')}
        >
          {message.text}
        </div>
        {staffName && (
          <p className="mt-1 text-right text-xs font-medium text-foreground">
            {staffName}
            {internals && staff?.surface === 'portal' ? ' (via client portal)' : ''}
          </p>
        )}
        {unreviewed && (
          <p className="mt-1 text-right text-xs font-medium text-tone-warning-foreground">
            Sent automatically — nobody read this first
          </p>
        )}
        {message.followUp && (
          <p className="mt-1 text-right text-xs font-medium text-muted-foreground">
            Follow-up (
            {message.followUp.sequence === 'no_response' ? 'no response' : 'conversation incomplete'}
            , step {message.followUp.step + 1})
          </p>
        )}
        {internals && message.fixedKind && (
          <p className="mt-1 text-right text-xs text-muted-foreground">
            Fixed wording: {message.fixedKind.replace(/_/g, ' ')}
          </p>
        )}
        <p
          className={`mt-1 text-xs ${inbound ? 'text-left' : 'text-right'} ${
            meta?.tone === 'danger' ? 'text-destructive' : 'text-muted-foreground'
          }`}
        >
          {formatWhen(message.sentAt ?? message.createdAt)}
          {meta ? ` · ${meta.label}` : ''}
          {internals && message.status === 'sent' && message.approvedByName
            ? ` · approved by ${message.approvedByName}`
            : ''}
          {internals && message.failureReason ? ` · ${message.failureReason}` : ''}
        </p>
      </div>
    </div>
  );
}

export function Transcript({
  conversationId,
  messages,
  internals,
  emptyText = 'Nothing has been said yet.',
}: {
  conversationId: string;
  messages: TranscriptMessage[];
  internals: boolean;
  emptyText?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrolledFor = useRef<string | null>(null);

  // ⚠️ Moves ONLY this element's scrollTop (the scroll-into-view API scrolls every scrollable
  // ancestor, and the shell is overflow-hidden). Open = jump to the bottom; a new message follows
  // only if the reader was already near the bottom, so the poll never yanks them.
  const messageCount = messages.length;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const opened = lastScrolledFor.current !== conversationId;
    if (opened || isNearBottom(el)) el.scrollTop = el.scrollHeight;
    lastScrolledFor.current = conversationId;
  }, [messageCount, conversationId]);

  return (
    <div
      ref={scrollRef}
      data-testid="transcript-scroll"
      className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 scroll-subtle"
    >
      {messages.length === 0 && <p className="text-sm text-muted-foreground">{emptyText}</p>}
      {messages.map((message) => (
        <TranscriptBubble key={message.id} message={message} internals={internals} />
      ))}
    </div>
  );
}
